import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useAuthModal } from "../context/AuthModalContext";
import { motion, AnimatePresence } from "motion/react";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "Hindi" },
  { code: "ta", label: "Tamil" },
  { code: "te", label: "Telugu" },
  { code: "mr", label: "Marathi" },
  { code: "gu", label: "Gujarati" },
  { code: "kn", label: "Kannada" },
  { code: "bn", label: "Bengali" },
  { code: "ml", label: "Malayalam" },
  { code: "pa", label: "Punjabi" },
];

const TOOL_KEYS = [
  { titleKey: "tools.capitalGain", icon: "📈", link: "/tools/capital-gain", freeKey: "tools.paid", free: false },
  { titleKey: "tools.tds", icon: "🧮", link: "/tools/tds-calculator", freeKey: "tools.free", free: true },
  { titleKey: "tools.bankStatement", icon: "🏦", link: "/tools/bank-statement", freeKey: "tools.paid", free: false },
  { titleKey: "tools.propertyGain", icon: "🏠", link: "/tools/property-gain", freeKey: "tools.free", free: true },
  { titleKey: "tools.overseasTds", icon: "🌏", link: "/tools/overseas-tds", freeKey: "tools.paid", free: false },
  { titleKey: "tools.cryptoGain", icon: "🪙", link: "/tools/crypto-gain", freeKey: "tools.comingSoon", free: false },
];

const FREE_TOOL_KEYS = TOOL_KEYS.filter(t => t.free);

const dropdownVariants = {
  hidden: { opacity: 0, y: -8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.15, ease: "easeOut" as const } },
  exit:    { opacity: 0, y: -8, transition: { duration: 0.12, ease: "easeIn" as const } },
};

function ToolsDropdown({ toolKeys, onClose }: { toolKeys: typeof TOOL_KEYS; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <motion.div
      variants={dropdownVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      style={{
        position: "absolute", top: "calc(100% + 8px)", left: 0,
        background: "white", border: "1.5px solid #EAE6E0",
        borderRadius: "14px", padding: "16px",
        minWidth: "520px", zIndex: 300,
        boxShadow: "0 8px 40px rgba(0,0,0,0.15)",
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px",
      }}
    >
      {toolKeys.map((tool, idx) => (
        <Link key={idx} to={tool.link} onClick={onClose}
          style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", borderRadius: "8px", textDecoration: "none", transition: "background 0.15s", background: "transparent" }}
          onMouseEnter={e => { e.currentTarget.style.background = "#F5F2EE"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
          <span style={{ fontSize: "22px", flexShrink: 0 }}>{tool.icon}</span>
          <div>
            <div style={{ fontSize: "13px", fontWeight: 600, color: "#2C2C2C" }}>{t(tool.titleKey)}</div>
            <div style={{ fontSize: "11px", color: tool.freeKey === "tools.free" ? "#375623" : tool.freeKey === "tools.comingSoon" ? "#5C5C5C" : "#7F6000", fontWeight: 600 }}>
              {t(tool.freeKey)}
            </div>
          </div>
        </Link>
      ))}
    </motion.div>
  );
}

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const { user, picture, logout } = useAuth();
  const { openModal } = useAuthModal();
  const [profileOpen, setProfileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [allToolsOpen, setAllToolsOpen] = useState(false);
  const [freeToolsOpen, setFreeToolsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [lang, setLang] = useState(localStorage.getItem("language") || "en");
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setProfileOpen(false);
        setLangOpen(false);
      }
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setAllToolsOpen(false);
        setFreeToolsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const initials = user ? (user.name || user.email || "U")[0].toUpperCase() : "U";
  const navLink: React.CSSProperties = {
    color: "#D0CCC8", fontSize: "14px", fontWeight: "500",
    transition: "color 0.2s", textDecoration: "none",
    cursor: "pointer", background: "none", border: "none", padding: 0,
  };
  const isPricingActive = location.pathname === "/pricing";

  return (
    <nav style={{
      background: scrolled ? "rgba(10,10,10,0.85)" : "rgba(20,20,20,0.5)",
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      borderBottom: scrolled ? "1px solid rgba(201,168,76,0.15)" : "1px solid rgba(255,255,255,0.05)",
      boxShadow: scrolled ? "0 4px 24px rgba(0,0,0,0.3)" : "none",
      position: "sticky",
      top: 0,
      zIndex: 100,
      transition: "background 0.3s, border-color 0.3s, box-shadow 0.3s",
    }}>

      {/* Top bar */}
      <div style={{ padding: "0 32px", height: "60px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>

        <Link to={user ? "/dashboard" : "/"} style={{ display: "flex", alignItems: "center", gap: "10px", textDecoration: "none" }}>
          <div className="logo-shine" style={{ background: "#C9A84C", width: "36px", height: "36px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "18px", color: "#2C2C2C", fontStyle: "italic" }}>T</div>
          <span style={{ color: "white", fontWeight: 700, fontSize: "18px" }}>Tax<span style={{ color: "#C9A84C" }}>Thinker</span></span>
        </Link>

        {/* Desktop nav links */}
        <div className="desktop-nav-links" style={{ display: "flex", alignItems: "center", gap: "28px", position: "relative" }} ref={navRef}>
          <div style={{ position: "relative" }}>
            <button onClick={() => { setAllToolsOpen(!allToolsOpen); setFreeToolsOpen(false); }}
              style={{ ...navLink, color: allToolsOpen ? "#C9A84C" : "#D0CCC8", display: "flex", alignItems: "center", gap: "4px" }}
              onMouseEnter={e => { e.currentTarget.style.color = "#C9A84C"; }}
              onMouseLeave={e => { if (!allToolsOpen) e.currentTarget.style.color = "#D0CCC8"; }}>
              {t("nav.allTools")} <span style={{ fontSize: "9px" }}>▼</span>
            </button>
            <AnimatePresence>
              {allToolsOpen && <ToolsDropdown toolKeys={TOOL_KEYS} onClose={() => setAllToolsOpen(false)} />}
            </AnimatePresence>
          </div>

          <div style={{ position: "relative" }}>
            <button onClick={() => { setFreeToolsOpen(!freeToolsOpen); setAllToolsOpen(false); }}
              style={{ ...navLink, color: freeToolsOpen ? "#C9A84C" : "#D0CCC8", display: "flex", alignItems: "center", gap: "4px" }}
              onMouseEnter={e => { e.currentTarget.style.color = "#C9A84C"; }}
              onMouseLeave={e => { if (!freeToolsOpen) e.currentTarget.style.color = "#D0CCC8"; }}>
              {t("nav.freeTools")} <span style={{ fontSize: "9px" }}>▼</span>
            </button>
            <AnimatePresence>
              {freeToolsOpen && <ToolsDropdown toolKeys={FREE_TOOL_KEYS} onClose={() => setFreeToolsOpen(false)} />}
            </AnimatePresence>
          </div>

          <div style={{ position: "relative" }}>
            <Link to="/pricing"
              style={{ ...navLink, display: "flex", alignItems: "center", color: isPricingActive ? "#C9A84C" : "#D0CCC8" } as React.CSSProperties}
              onMouseEnter={e => { e.currentTarget.style.color = "#C9A84C"; }}
              onMouseLeave={e => { if (!isPricingActive) e.currentTarget.style.color = "#D0CCC8"; }}>
              {t("nav.pricing")}
            </Link>
            {isPricingActive && (
              <motion.div
                layoutId="nav-underline"
                style={{ position: "absolute", bottom: "-4px", left: 0, right: 0, height: "2px", background: "#C9A84C", borderRadius: "1px" }}
                transition={{ duration: 0.2 }}
              />
            )}
          </div>
        </div>

        {/* Desktop right controls */}
        <div className="desktop-right-controls" style={{ display: "flex", alignItems: "center", gap: "14px" }} ref={ref}>

          {/* Accessibility icon — always links to accessibility page */}
          <Link
            to="/account/accessibility"
            title="Accessibility Settings"
            style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(201,168,76,0.3)", borderRadius: "6px", width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", color: "#C9A84C", fontSize: "16px", textDecoration: "none", transition: "background 0.2s" }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(201,168,76,0.15)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
          >
            ♿
          </Link>

          <button onClick={toggleTheme} title="Toggle dark mode" style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(201,168,76,0.3)", borderRadius: "20px", width: "48px", height: "26px", cursor: "pointer", position: "relative", display: "flex", alignItems: "center", padding: "3px" }}>
            <div style={{ width: "20px", height: "20px", borderRadius: "50%", background: "#C9A84C", transform: theme === "dark" ? "translateX(22px)" : "translateX(0)", transition: "transform 0.25s", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px" }}>
              {theme === "dark" ? "🌙" : "☀️"}
            </div>
          </button>

          <div style={{ position: "relative" }}>
            <button onClick={() => { setLangOpen(!langOpen); setProfileOpen(false); }} style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(201,168,76,0.4)", color: "#C9A84C", borderRadius: "6px", padding: "5px 10px", fontSize: "13px", cursor: "pointer", display: "flex", alignItems: "center", gap: "5px" }}>
              {LANGUAGES.find(l => l.code === lang)?.label} <span style={{ fontSize: "9px" }}>▼</span>
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div
                  variants={dropdownVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", background: "#2C2C2C", border: "1px solid #C9A84C", borderRadius: "8px", overflow: "hidden", minWidth: "140px", zIndex: 300 }}
                >
                  {LANGUAGES.map(l => (
                    <button key={l.code} onClick={() => { i18n.changeLanguage(l.code); localStorage.setItem("language", l.code); setLang(l.code); setLangOpen(false); }}
                      style={{ display: "block", width: "100%", padding: "8px 14px", textAlign: "left", background: l.code === lang ? "rgba(201,168,76,0.15)" : "transparent", color: l.code === lang ? "#C9A84C" : "#D0CCC8", border: "none", cursor: "pointer", fontSize: "13px", borderBottom: "0.5px solid rgba(255,255,255,0.05)" }}>
                      {l.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {user ? (
            <div style={{ position: "relative" }}>
              <button onClick={() => { setProfileOpen(!profileOpen); setLangOpen(false); }}
                style={{ width: "38px", height: "38px", borderRadius: "50%", background: "#C9A84C", border: "2px solid #A07828", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "15px", color: "#2C2C2C", overflow: "hidden", padding: 0 }}>
                {picture ? (
                  <img src={picture} alt="Profile" style={{ width: "38px", height: "38px", borderRadius: "50%", objectFit: "cover" }} />
                ) : initials}
              </button>
              <AnimatePresence>
                {profileOpen && (
                  <motion.div
                    variants={dropdownVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", background: "var(--white)", border: "1px solid var(--border)", borderRadius: "12px", minWidth: "220px", zIndex: 300, overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}
                  >
                    <div style={{ padding: "16px 16px 14px", borderBottom: "1px solid var(--border)", background: "var(--offwhite)", textAlign: "center" }}>
                      {picture ? (
                        <img src={picture} alt="Profile" style={{ width: "56px", height: "56px", borderRadius: "50%", objectFit: "cover", border: "2px solid #C9A84C", display: "block", margin: "0 auto 10px" }} />
                      ) : (
                        <div style={{ width: "56px", height: "56px", borderRadius: "50%", background: "#C9A84C", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "22px", color: "#2C2C2C", margin: "0 auto 10px" }}>{initials}</div>
                      )}
                      <div style={{ fontWeight: 600, fontSize: "14px", color: "var(--text-primary)" }}>{user.name || "User"}</div>
                      <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>{user.email}</div>
                      <span style={{ fontSize: "11px", background: "#C9A84C", color: "#2C2C2C", padding: "2px 8px", borderRadius: "10px", fontWeight: 700, display: "inline-block", marginTop: "6px" }}>
                        {(user.plan || "free").toUpperCase()}
                      </span>
                    </div>
                    {[
                      { labelKey: "account.settings", path: "/account/settings", icon: "⚙" },
                      { labelKey: "account.reports", path: "/account/reports", icon: "📄" },
                      { labelKey: "account.accessibility", path: "/account/accessibility", icon: "♿" },
                      { labelKey: "account.plans", path: "/account/plans", icon: "💳" },
                    ].map(item => {
                      const isActive = location.pathname === item.path;
                      return (
                        <Link key={item.path} to={item.path} onClick={() => setProfileOpen(false)}
                          style={{ display: "flex", alignItems: "center", gap: "10px", padding: "11px 16px", fontSize: "14px", color: isActive ? "#C9A84C" : "var(--text-primary)", borderBottom: "0.5px solid var(--border)", transition: "background 0.15s", background: isActive ? "rgba(201,168,76,0.06)" : "transparent", textDecoration: "none", fontWeight: isActive ? 600 : 400 }}
                          onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "var(--offwhite)"; }}
                          onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}>
                          <span style={{ fontSize: "14px" }}>{item.icon}</span>
                          <span style={{ flex: 1 }}>{t(item.labelKey)}</span>
                          {isActive && <span style={{ color: "#C9A84C", fontSize: "8px" }}>●</span>}
                        </Link>
                      );
                    })}
                    <div style={{ height: "1px", background: "var(--border)" }} />
                    <button onClick={logout} style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", padding: "11px 16px", textAlign: "left", fontSize: "14px", color: "var(--error)", background: "transparent", border: "none", cursor: "pointer", fontWeight: 500 }}>
                      <span>🚪</span> {t("account.logout")}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <motion.button
              onClick={openModal}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{ background: "#C9A84C", color: "#2C2C2C", border: "none", padding: "8px 18px", borderRadius: "8px", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}
            >
              {t("nav.login")}
            </motion.button>
          )}
        </div>

        {/* Hamburger — mobile only */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="mobile-menu-btn"
          style={{ display: "none", background: "transparent", border: "none", cursor: "pointer", padding: "8px", color: "white", fontSize: "20px", alignItems: "center", justifyContent: "center" }}
        >
          {mobileOpen ? "✕" : "☰"}
        </button>
      </div>

      {/* Mobile menu panel */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            style={{ overflow: "hidden", borderTop: "1px solid rgba(201,168,76,0.15)" }}
          >
            <div style={{ padding: "12px 24px 20px", display: "flex", flexDirection: "column", gap: "2px" }}>
              {TOOL_KEYS.map((tool, idx) => (
                <Link key={idx} to={tool.link}
                  onClick={() => setMobileOpen(false)}
                  style={{ display: "flex", alignItems: "center", gap: "10px", padding: "11px 12px", borderRadius: "8px", color: "#D0CCC8", fontSize: "14px", fontWeight: 500, textDecoration: "none", transition: "background 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.07)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                  <span>{tool.icon}</span>
                  <span>{t(tool.titleKey)}</span>
                </Link>
              ))}
              <Link to="/pricing"
                onClick={() => setMobileOpen(false)}
                style={{ display: "flex", alignItems: "center", gap: "10px", padding: "11px 12px", borderRadius: "8px", color: isPricingActive ? "#C9A84C" : "#D0CCC8", fontSize: "14px", fontWeight: 500, textDecoration: "none" }}>
                <span>💰</span>
                <span>{t("nav.pricing")}</span>
              </Link>

              <div style={{ height: "1px", background: "rgba(255,255,255,0.08)", margin: "10px 0" }} />

              <div style={{ padding: "0 12px 8px" }}>
                <div style={{ fontSize: "11px", color: "#606060", marginBottom: "8px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Language</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {LANGUAGES.map(l => (
                    <button key={l.code}
                      onClick={() => { i18n.changeLanguage(l.code); localStorage.setItem("language", l.code); setLang(l.code); }}
                      style={{ padding: "4px 10px", borderRadius: "6px", fontSize: "12px", background: l.code === lang ? "#C9A84C" : "rgba(255,255,255,0.08)", color: l.code === lang ? "#2C2C2C" : "#D0CCC8", border: "none", cursor: "pointer", fontWeight: l.code === lang ? 600 : 400 }}>
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ height: "1px", background: "rgba(255,255,255,0.08)", margin: "4px 0 10px" }} />

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 12px" }}>
                <button onClick={toggleTheme} title="Toggle dark mode" style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(201,168,76,0.3)", borderRadius: "20px", width: "48px", height: "26px", cursor: "pointer", position: "relative", display: "flex", alignItems: "center", padding: "3px" }}>
                  <div style={{ width: "20px", height: "20px", borderRadius: "50%", background: "#C9A84C", transform: theme === "dark" ? "translateX(22px)" : "translateX(0)", transition: "transform 0.25s", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px" }}>
                    {theme === "dark" ? "🌙" : "☀️"}
                  </div>
                </button>
                {user ? (
                  <button onClick={logout} style={{ fontSize: "13px", color: "var(--error)", background: "transparent", border: "none", cursor: "pointer", fontWeight: 500 }}>
                    {t("account.logout")}
                  </button>
                ) : (
                  <motion.button
                    onClick={() => { setMobileOpen(false); openModal(); }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    style={{ background: "#C9A84C", color: "#2C2C2C", padding: "8px 18px", borderRadius: "8px", fontSize: "13px", fontWeight: 700, border: "none", cursor: "pointer" }}
                  >
                    {t("nav.login")}
                  </motion.button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
